import { fusionner } from '@artosera/shared';
import type { GenreContrat, Langue } from '@artosera/shared';
import {
  DEMO_MODELES_CONTRATS,
  DEMO_CONTRATS,
  DEMO_CONTRATS_ARTISTES,
} from './demoContratsFixtures.js';
import type { CorpsGeneration, CorpsModeleContrat } from './contrats.js';
import {
  DEMO_ARTISTES,
  DEMO_CONTACTS,
  DEMO_OEUVRES,
  DEMO_PDF_DOCUMENT_DATA_URI,
} from './demoFixtures.js';
const modeles = structuredClone(DEMO_MODELES_CONTRATS),
  contrats = structuredClone(DEMO_CONTRATS),
  artistes = structuredClone(DEMO_CONTRATS_ARTISTES);
const document = () => ({
  url: DEMO_PDF_DOCUMENT_DATA_URI,
  expireLe: new Date(Date.now() + 300000).toISOString(),
  expireDansSecondes: 300,
  nomFichier: 'contrat.pdf',
});
const pagination = (total: number) => ({ page: 1, parPage: 100, total, pages: 1 });
function apercu(c: CorpsGeneration, genre: GenreContrat, langue: Langue) {
  const modele = modeles.find((m) => m.genre === genre && m.langue === langue);
  if (!modele) return undefined;
  const artiste = DEMO_ARTISTES.find((a) => a.id === c.artisteId),
    contact = DEMO_CONTACTS.find((a) => a.id === c.contactId);
  const valeurs: Record<string, string> = {
    'galerie.raisonSociale': 'Galerie Sainte-Avoye',
    'galerie.adresse': '3 rue Sainte-Avoye, Paris',
    'galerie.ville': 'Paris',
    'galerie.siren': '000000000',
    'contrat.date': new Intl.DateTimeFormat(langue, { dateStyle: 'long', timeZone: 'UTC' }).format(
      new Date(),
    ),
    'contrat.numero': 'CT-…',
    ...(c.saisies ?? {}),
  };
  if (artiste) {
    valeurs['artiste.nom'] = artiste.nom;
    valeurs['artiste.nationalite'] = artiste.nationalite ?? 'FR';
  }
  if (contact) {
    valeurs['contact.nom'] = contact.nomAffiche;
    valeurs['contact.adresse'] = '1 rue Exemple, Paris';
  }
  if (c.oeuvres?.length)
    valeurs['oeuvres.liste'] = c.oeuvres
      .map((id) => DEMO_OEUVRES.find((o) => o.id === id)?.titre ?? '')
      .join('\n');
  const manquants = new Set<string>();
  const f = (t: string) => {
    const r = fusionner(t, valeurs);
    r.manquants.forEach((c) => manquants.add(c));
    return r.texte;
  };
  return {
    titre: f(modele.titre),
    preambule: f(modele.preambule),
    articles: modele.articles.map((a) => ({ titre: f(a.titre), texte: f(a.texte) })),
    fait: f(modele.fait),
    valeurs,
    manquants: [...manquants],
  };
}
export function reponseDemoContrats(
  path: string,
  params: URLSearchParams,
  methode: string,
  corps?: unknown,
): unknown | undefined {
  const m =
    /^\/modeles-contrats\/modeles\/(nda|depot|artiste)\/(fr|en)(?:\/(apercu|reinitialiser))?$/.exec(
      path,
    );
  if (path === '/modeles-contrats/modeles' && methode === 'GET') return { modeles };
  if (m) {
    const genre = m[1] as GenreContrat,
      langue = m[2] as Langue;
    const modele = modeles.find((x) => x.genre === genre && x.langue === langue);
    if (!modele) return undefined;
    if (m[3] === 'apercu') return apercu((corps ?? {}) as CorpsGeneration, genre, langue);
    if (methode === 'PUT')
      Object.assign(modele, corps as CorpsModeleContrat, { version: modele.version + 1 });
    if (m[3] === 'reinitialiser') {
      const defaut = DEMO_MODELES_CONTRATS.find((x) => x.genre === genre && x.langue === langue);
      Object.assign(modele, structuredClone(defaut), { version: modele.version + 1 });
    }
    return { modele };
  }
  if (path === '/modeles-contrats/contrats') {
    if (methode === 'GET') {
      const liste = contrats.filter(
        (c) =>
          (!params.get('genre') || c.genre === params.get('genre')) &&
          (!params.get('artisteId') || c.artisteId === params.get('artisteId')) &&
          (!params.get('contactId') || c.contactId === params.get('contactId')),
      );
      return { contrats: liste, pagination: pagination(liste.length) };
    }
    const c = corps as CorpsGeneration;
    const a = apercu(c, c.genre, c.langue ?? 'fr');
    if (!a || a.manquants.length) return undefined;
    const contrat = {
      ...DEMO_CONTRATS[1]!,
      ...a,
      id: `contrat-${Date.now()}`,
      numero: `CT-2026-${String(contrats.length + 1).padStart(4, '0')}`,
      genre: c.genre,
      langue: c.langue ?? 'fr',
      etat: 'genere' as const,
      artisteId: c.artisteId ?? null,
      contactId: c.contactId ?? null,
      oeuvres: c.oeuvres ?? [],
      emisLe: new Date().toISOString(),
    };
    contrats.push(contrat);
    return { contrat, document: document() };
  }
  const f = /^\/modeles-contrats\/contrats\/([^/]+)(?:\/(pdf|signe|annuler|coffre))?$/.exec(path);
  if (f) {
    const contrat = contrats.find((c) => c.id === f[1]);
    if (!contrat) return undefined;
    const c = (corps ?? {}) as { signeLe?: string; motif?: string };
    if (f[2] === 'pdf') return document();
    if (f[2] === 'signe') {
      contrat.etat = 'signe';
      contrat.signeLe = c.signeLe ?? new Date().toISOString();
    }
    if (f[2] === 'annuler') contrat.etat = 'annule';
    if (f[2] === 'coffre') {
      contrat.documentId ??= `doc-${contrat.id}`;
      return { documentId: contrat.documentId };
    }
    return { contrat };
  }
  if (path === '/contrats-artistes') {
    if (methode === 'GET') {
      const liste = artistes.filter(
        (c) =>
          (!params.get('artisteId') || c.artisteId === params.get('artisteId')) &&
          (!params.get('etat') || c.etat === params.get('etat')),
      );
      return { contrats: liste, pagination: pagination(liste.length) };
    }
    const contrat = {
      ...DEMO_CONTRATS_ARTISTES[2]!,
      ...(corps as object),
      id: `contrat-artiste-${Date.now()}`,
      etat: 'brouillon' as const,
    };
    artistes.push(contrat);
    return { contrat };
  }
  const ac = /^\/contrats-artistes\/([^/]+)(?:\/(vigueur|resilier|document|contrat-genere))?$/.exec(
    path,
  );
  if (ac) {
    const contrat = artistes.find((c) => c.id === ac[1]);
    if (!contrat) return undefined;
    const c = (corps ?? {}) as {
      finLe?: string;
      motif?: string;
      resilieLe?: string;
      documentId?: string | null;
      contratId?: string | null;
    };
    if (methode === 'PATCH') Object.assign(contrat, c);
    if (ac[2] === 'vigueur') contrat.etat = 'en_vigueur';
    if (ac[2] === 'resilier') {
      contrat.etat = 'resilie';
      contrat.motifResiliation = c.motif ?? '';
      contrat.resilieLe = c.resilieLe ?? new Date().toISOString();
    }
    if (ac[2] === 'document') contrat.documentId = c.documentId ?? null;
    if (ac[2] === 'contrat-genere') contrat.contratGenereId = c.contratId ?? null;
    contrat.joursAvantEcheance = contrat.finLe
      ? Math.ceil((new Date(contrat.finLe).getTime() - Date.now()) / 86400000)
      : null;
    contrat.echu =
      contrat.etat === 'en_vigueur' &&
      contrat.joursAvantEcheance !== null &&
      contrat.joursAvantEcheance < 0;
    return { contrat };
  }
  return undefined;
}
